/**
 * Scenario Studio ABP feature names + defaults.
 * Ceiling only — session type / package still decides what a given board offers.
 *
 * Missing application-configuration values use these defaults (no on→off flash).
 */

export const StudioFeature = {
  studio: 'Buxus.Scenarios',
  timeline: 'Buxus.Scenarios.Timeline',
  timelineEdit: 'Buxus.Scenarios.Timeline.Edit',
  timelineKeyframing: 'Buxus.Scenarios.Timeline.Keyframing',
  showOnBoard: 'Buxus.Scenarios.Inspect.ShowOnBoard',
  findAssets: 'Buxus.Scenarios.Inspect.FindAssets',
  exerciseRuns: 'Buxus.Scenarios.Exercise.Runs',
  rolePlay: 'Buxus.Scenarios.RolePlay',
  comments: 'Buxus.Scenarios.Comments',
  publicAudience: 'Buxus.Scenarios.Sharing.PublicAudience',
  anyoneWithLink: 'Buxus.Scenarios.Sharing.AnyoneWithLink',
  qrCode: 'Buxus.Scenarios.Sharing.QrCode',
  operatorStyles: 'Buxus.Scenarios.Map.OperatorStyles',
  versions: 'Buxus.Scenarios.Versions',
  /** Defined for a later kill switch — not read by UI yet. */
  windAndPlumes: 'Buxus.Scenarios.Weather.WindAndPlumes',
  pdfExports: 'Buxus.Scenarios.Exports.Pdf',
  inviteAttachDocs: 'Buxus.Scenarios.Invites.AttachDocs',
} as const

export type StudioFeatureName = (typeof StudioFeature)[keyof typeof StudioFeature]

export const STUDIO_FEATURE_DEFAULTS: Record<StudioFeatureName, boolean> = {
  [StudioFeature.studio]: true,
  [StudioFeature.timeline]: true,
  [StudioFeature.timelineEdit]: true,
  [StudioFeature.timelineKeyframing]: true,
  [StudioFeature.showOnBoard]: true,
  [StudioFeature.findAssets]: false,
  [StudioFeature.exerciseRuns]: true,
  [StudioFeature.rolePlay]: true,
  [StudioFeature.comments]: true,
  [StudioFeature.publicAudience]: true,
  [StudioFeature.anyoneWithLink]: true,
  [StudioFeature.qrCode]: true,
  [StudioFeature.operatorStyles]: true,
  [StudioFeature.versions]: true,
  [StudioFeature.windAndPlumes]: true,
  [StudioFeature.pdfExports]: true,
  [StudioFeature.inviteAttachDocs]: true,
}

function parentCeiling(name: string): string | null {
  if (name.startsWith(`${StudioFeature.timeline}.`)) return StudioFeature.timeline
  return null
}

/** Feature ∧ parent area. Pass ABP `featureEnabled` (uses catalog default when unset). */
export function studioFeatureOn(
  featureEnabled: (name: string, fallback?: boolean) => boolean,
  name: StudioFeatureName,
): boolean {
  const fallback = STUDIO_FEATURE_DEFAULTS[name]
  const parent = parentCeiling(name)
  if (parent && !featureEnabled(parent, STUDIO_FEATURE_DEFAULTS[parent as StudioFeatureName])) {
    return false
  }
  return featureEnabled(name, fallback)
}
